import { Vue, Options } from 'vue-class-component';
import {
  AddressingType,
  BuildingDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
  EPDeviceTypeEnum,
  GateConfigDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import ButtonSelection from '@/views/components/ButtonSelection/ButtonSelection.vue';
import { SET_SELECTED_PLANT_ID } from '@/core/store/action-types';

@Options({
  name: 'DialogEntrancePlantCreateStep7',
  components: {
    ButtonSelection,
  },
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },
    structureCreate: {
      required: false,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    plant: function (newValue) {
      if (newValue) this.initializeData();
    },
    "currentGate.scsAddress"() {
      this.verifyValidityOfScsAddress();
    },
    "currentGate.newPeSelected": function (newVal) {
      if (newVal) {
        this.smartReaderDisabled = true;
        this.keypadDisabled = false;
      } else if (!this.currentGate.keypadSelected) {
        this.smartReaderDisabled = false;
      }
    },
    "currentGate.keypadSelected": function (newVal) {
      if (newVal) {
        this.smartReaderDisabled = true;
        this.newPeDisabled = false;
      } else if (!this.currentGate.newPeSelected) {
        this.smartReaderDisabled = false;
      }
    },
    "currentGate.smartReaderSelected": function (newVal) {
      if (newVal) {
        this.newPeDisabled = true;
        this.keypadDisabled = true;
      } else {
        this.newPeDisabled = false;
        this.keypadDisabled = false;
      }
    },
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
  computed: {},
})
export default class DialogEntrancePlantCreateStep7 extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  currentGateIndex = 0;
  gateConfig: { [key: string]: GateConfigDTO } = {};
  readonly MIN_SCS_ADDRESS = 0;
  readonly MAX_SCS_ADDRESS = 95;
  scsAddrNotValid: boolean = false;
  invalidScsReason: string = '';

  changingGate = false;

  readonly AllBuildingsValue = "All_buildings";
  readonly AllRisersValue = "All_risers";
  
  allBuildings: {[key: string]: boolean} = {};
  allRisers: { [key: string]: { [innerKey: string]: boolean } } = {};
  selectedBuildingIds:{ [key: string]: string[] } = {};
  selectedRiserIds: { [key: string]: { [innerKey: string]: string[] } } = {};

  get selectedBuildings(): BuildingDTO[] | null {
    if (!this.plant.buildings) return null;
    return this.plant.buildings.filter(b => this.selectedBuildingIds[this.currentGateId!].includes(b.buildingId!));
  }

  get currentGateId(): string | null {
    if (!this.plant.gates) return null;
    return this.plant.gates[this.currentGateIndex].gateId ?? null;
  }

  get currentGateScs(): number {
    return this.plant.gates![this.currentGateIndex].scsAddress!;
  }

  get currentGate(): GateConfigDTO | null {
    if (!this.plant.gates) return null;
    const gateId = this.currentGateId;
    if (!gateId) return null;
    const g = this.gateConfig[gateId];
    return g;
  }
  get isBRFA() { 
    return this.plant.addressingType==AddressingType.BuildingRiserFloorApartment;
  }

  get pageTitle() {
    return `${this.$t('AppResourceEP.SetupGateTitle').toString()} ${
      this.currentGateIndex + 1
    }`;
  }

  mounted(): void {
    this.initializeData();
  }

  initializeData() {
    if (!this.plant.gates) return; 
    for (const gate of this.plant.gates) {
      this.selectedBuildingIds[gate.gateId!] = [];
      this.selectedRiserIds[gate.gateId!] = {};
      this.allBuildings[gate.gateId!] = false;
      this.allRisers[gate.gateId!] = {};
      this.plant.buildings?.forEach((building) => {
        this.selectedRiserIds[gate.gateId!][building.buildingId!] = [];
        this.allRisers[gate.gateId!][building.buildingId!] = false;
       });
      gate!.connectedBuildingIds!.forEach((buildingId) => {
        this.selectedRiserIds[gate.gateId!][buildingId] = [];
        this.allRisers[gate.gateId!][buildingId] = false;
      });
      this.gateConfig[gate.gateId!] = new GateConfigDTO({
        scsAddress: gate.scsAddress,
        gateName: gate.name,
        newPeSelected:  gate.devices?.some((d) => d.type == EPDeviceTypeEnum.Linea5000) ?? false,
        keypadSelected: gate.devices?.some((d) => d.type == EPDeviceTypeEnum.KeyPad) ?? false,
        smartReaderSelected:  gate.devices?.some((d) => d.type == EPDeviceTypeEnum.SmartAccess) ?? false,
        connectedBuildingIds: gate.connectedBuildingIds ?? [],
        connectedRiserIds: gate.connectedRiserIds ?? [],
        connectedApartmentsIds: gate.apartments ?? [],
      });
    }
    this.buildForm();                                
    this.refreshDeviceSelection();
  }
  buildForm() {
    if (this.plant.gates && this.plant.gates.length > 0) {
      for (const gate of this.plant.gates) {
        if (gate.connectedBuildingIds) {
          gate.connectedBuildingIds!.forEach((buildingId) => {
            this.selectedBuildingIds[gate.gateId!].push(buildingId);
            let buildings = this.plant.buildings!.filter(b => b.buildingId == buildingId);
            let building = buildings.length > 0 ? buildings[0] : null;
              if (building) {
              gate.connectedRiserIds!.forEach(riserId => {
                if (!this.selectedRiserIds[gate.gateId!][buildingId].includes(riserId!) && building!.risers!.some(r => r.riserId == riserId!)) {
                  this.selectedRiserIds[gate.gateId!][buildingId].push(riserId);
                }
              });
            }
          });
        }
      }
    }
  }

  getDataToSave() {
    return new EntrancePlantStructureCreateDTO({
      wizardStep: 3,
      gateConfigStructure: this.gateConfig,
    });
  }

  checkInputValidity() {
    if(this.currentGate) {
      if(this.currentGate.scsAddress! < this.MIN_SCS_ADDRESS)this.currentGate.scsAddress = this.MIN_SCS_ADDRESS
      if(this.currentGate.scsAddress! > this.MAX_SCS_ADDRESS)this.currentGate.scsAddress = this.MAX_SCS_ADDRESS
    }
  }

  verifyValidityOfScsAddress() {
    if(this.currentGate) {
      if (this.currentGate.scsAddress === undefined) return false;
      
      const value = Number(this.currentGate.scsAddress);

      const scsAddressesPlusCurrentAdress = this.plant.scsAddressHelper!.availableScsAddressGate!.map(addr => addr) ?? [];
      scsAddressesPlusCurrentAdress?.push(this.currentGateScs);

      const acceptedRange = value >= this.MIN_SCS_ADDRESS && value <= this.MAX_SCS_ADDRESS;
      const isItAvailable = scsAddressesPlusCurrentAdress.includes(value) ?? false;
      this.scsAddrNotValid = !acceptedRange || !isItAvailable;
      if (!acceptedRange) {
        this.invalidScsReason = this.$t('AppResourceEP.GateScsAddressOutOfRange');
      } else if (!isItAvailable) {
        this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
      } else {
        this.invalidScsReason = '';
      }
    }
  }

  retrieveApartmentIds() {
    if (this.isBRFA)
    {
      const selectBuildings = (this.plant.buildings || [])
          .filter((b) => this.currentGate!.connectedBuildingIds?.includes(b.buildingId!));
      const selectedApartments = selectBuildings
          .flatMap((b) => b.apartments || [])
          .filter((r) => r.riserId && this.currentGate!.connectedRiserIds?.includes(r.riserId))
          .map((a) => a.apartmentId!);
      this.currentGate!.connectedApartmentsIds = selectedApartments;
    } 
    else 
    {
      this.currentGate!.connectedApartmentsIds = this.plant.buildings
          ?.filter((b) => this.currentGate!.connectedBuildingIds?.includes(b.buildingId!))
          .flatMap((b) => b.apartments || [])
          .map((a) => a.apartmentId!) || [];
    }
  }

  async handleBackButton() {
    if (this.plant.gates) {
      if (this.currentGateIndex > 0) {
        await this.changeGate('back');
        this.refreshDeviceSelection();
        return;
      }
    }
    this.$emit('onBack', 'DialogEntrancePlantCreateStep6');
  }

  async handleNextButton() {
    if (this.currentGate) {
      this.currentGate.connectedRiserIds =[... new Set(Object.values(this.selectedRiserIds[this.currentGateId!]).flat())];
      this.currentGate.connectedBuildingIds = [...new Set(this.selectedBuildingIds[this.currentGateId!])];
      this.retrieveApartmentIds();
    }
    if (this.plant.gates) {
      if (this.currentGateIndex < this.plant.gates.length - 1) {
        await this.changeGate('next');
        this.refreshDeviceSelection();
        return;
      }
      await this.updatePlantStructure(this.getDataToSave());
    }
    this.goToPlantDetail();
  }

  async changeGate(direction: 'next' | 'back') {
    this.$emit('is-loading', true);
    this.changingGate = true;
    if (direction === 'next') {
      this.currentGateIndex++;
    } else {
      this.currentGateIndex--;
    }
    setTimeout(() => {
        this.changingGate = false;
        this.$emit('is-loading', false)
      }, 500);
  }

  goToPlantDetail() {
    this.$nextTick(async () => {
      await this.$store.dispatch(SET_SELECTED_PLANT_ID, this.plantId);
      this.$router.push({
        name: 'page-entranceplant-dashboard',
        params: { plantId: this.plantId.toString() ?? '' },
      });
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResource.PlantCreated'),
        closable: false,
        life: 3000,
      });
      const summary = this.$t('general.synced');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: summary,
          closable: false,
          life: 3000,
        });
      }, 1500);
    });
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);
    try {
      await plantsClient.structure(this.plant.desktopId!, structure);
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
  newPeDisabled = false;
  keypadDisabled = false;
  smartReaderDisabled = false;
  refreshDeviceSelection() {
    if (!this.currentGate) return;
    this.newPeDisabled = false;
    this.keypadDisabled = false;
    this.smartReaderDisabled = false;

    if(this.selectedBuildingIds[this.currentGateId!].length==this.plant.buildings?.length) {
      this.allBuildings[this.currentGateId!] = true;
    }
    this.selectedBuildingIds[this.currentGateId!].forEach((buildingId) => { 
      if(this.selectedRiserIds[this.currentGateId!][buildingId].length==this.plant.buildings?.filter(b => b.buildingId == buildingId)[0].risers?.length) {
        this.allRisers[this.currentGateId!][buildingId] = true;
      }
    });
  }

  get nextEnabled(): boolean {
    if (this.scsAddrNotValid) return false
    if (!this.currentGate || !this.selectedBuildingIds[this.currentGateId!]) return false;
    let devicesSelected= ((this.currentGate.newPeSelected == true || this.currentGate.keypadSelected == true || this.currentGate.smartReaderSelected == true));
    let buildingsSelected = this.selectedBuildingIds[this.currentGateId!].length > 0;
    let risersSelected = true;
    if (this.isBRFA) {
      //foreach building selected, check if one riser is selected
      this.selectedBuildingIds[this.currentGateId!].forEach((buildingId) => { 
        if (this.selectedRiserIds[this.currentGateId!] && this.selectedRiserIds[this.currentGateId!][buildingId]) {
          risersSelected = risersSelected && this.selectedRiserIds[this.currentGateId!][buildingId].length > 0;
         }
      });
      return devicesSelected && buildingsSelected && risersSelected;
    }
    return devicesSelected && buildingsSelected;
  }

  handleUpdateBuildingSelection(option: string, buildingId?: string) {
    if (option == this.AllBuildingsValue) {
      if (!this.allBuildings[this.currentGateId!]) {
        this.selectedBuildingIds[this.currentGateId!].splice(0);
        this.plant.buildings!.forEach(b => {
          this.selectedRiserIds[this.currentGateId!][b.buildingId!] = [];
          this.allRisers[this.currentGateId!][b.buildingId!] = false;
        });
      }
      else {
        this.plant.buildings!.forEach(element => {
          if(!this.selectedBuildingIds[this.currentGateId!].includes(element.buildingId!)) {
            this.selectedBuildingIds[this.currentGateId!].push(element.buildingId!);
            this.selectedRiserIds[this.currentGateId!][element.buildingId!] = [];
            this.allRisers[this.currentGateId!][element.buildingId!] = false;
          }
        });
      }
    }
    else if(buildingId) {
      if (!this.allBuildings[this.currentGateId!] && this.selectedBuildingIds[this.currentGateId!].length == this.plant.buildings?.length) {
        this.allBuildings[this.currentGateId!] = true;
        this.selectedRiserIds[this.currentGateId!][buildingId] = [];
        this.allRisers[this.currentGateId!][buildingId] = false;
      }
      else if (this.allBuildings[this.currentGateId!]) {
        this.allBuildings[this.currentGateId!] = false;
        this.selectedRiserIds[this.currentGateId!][buildingId] = [];
        this.allRisers[this.currentGateId!][buildingId] = false;
       }
    }
  }

  handleUpdateRiserSelection(option: string, buildingId: string) {
    let building = this.plant.buildings!.find(b => b.buildingId == buildingId);
    if (building) { 
      if (option == this.AllRisersValue) {
        if (!this.allRisers[this.currentGateId!][buildingId]) {
          this.selectedRiserIds[this.currentGateId!][buildingId].splice(0);
        }
        else {
          this.selectedRiserIds[this.currentGateId!][buildingId] = building.risers!.map(r => r.riserId!);
        }
      }
      else {
        if (!this.allRisers[this.currentGateId!][buildingId] && this.selectedRiserIds[this.currentGateId!][buildingId].length == building.risers!.length) {
          this.allRisers[this.currentGateId!][buildingId] = true;
        }
        else if (this.allRisers[this.currentGateId!][buildingId]) {
          this.allRisers[this.currentGateId!][buildingId] = false;
         }
       }
    }
  }
}
